import { parseModuleSchema } from "@frontend/pages/case-workspace/pathology/components/module-schema"
import type { ParsedModuleSchema, ProjectModuleItem, WsiSource } from "@frontend/pages/case-workspace/pathology/types"

/**
 * Overlay visibility in the pathology workspace follows the active
 * `label_raster` module:
 * - a declared overlay (`role === "overlay"`) is only ever attached when some
 *   `label_raster` module in the project edits its `domain`; overlays without
 *   a module are never shown;
 * - of the attached overlays, exactly the one whose `domain` matches the
 *   active `label_raster` module is visible. With any other module active no
 *   declared overlay is visible.
 */

/** Domains edited by the project's `label_raster` modules (deduplicated). */
export function getLabelRasterModuleDomains(modules: readonly ProjectModuleItem[]): ReadonlySet<string> {
  const domains = new Set<string>()
  for (const module of modules) {
    if (module.template_type !== "label_raster") continue
    const schema = parseModuleSchema(module.template_schema)
    if (schema?.type === "label_raster" && schema.domain) domains.add(schema.domain)
  }
  return domains
}

/** Domain the active module edits, or null when it is not a `label_raster` module. */
export function getActiveLabelRasterDomain(
  activeModule: Pick<ProjectModuleItem, "template_type"> | null,
  activeModuleSchema: ParsedModuleSchema
): string | null {
  if (activeModule?.template_type !== "label_raster") return null
  if (activeModuleSchema?.type !== "label_raster") return null
  return activeModuleSchema.domain || null
}

/** Declared overlays of `baseSeriesId` that some `label_raster` module edits. */
export function selectModuleBackedOverlays(
  sources: readonly WsiSource[],
  baseSeriesId: string | null,
  moduleDomains: ReadonlySet<string>
): WsiSource[] {
  if (!baseSeriesId) return []
  return sources.filter(
    (s) => s.role === "overlay" && s.overlayOf === baseSeriesId && !!s.domain && moduleDomains.has(s.domain)
  )
}

/** Whether a declared overlay belongs to the active `label_raster` module. */
export function isOverlayForActiveModule(source: Pick<WsiSource, "domain">, activeDomain: string | null): boolean {
  return activeDomain !== null && source.domain === activeDomain
}
